// Mar 2026 — Assortment Intelligence (Phase 1) — Admin read-only.
// Two tabs: Promemoria riordino (customer-level) + Rischio uscita gamma (SKU-level).
// No email actions, no writes.
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { Section, Card, Button, Alert, Badge } from "../design-system";

const STATUS_TONE = {
  WATCH: "neutral",
  RANGE_LOSS_RISK: "warning",
  PROBABLE_RANGE_LOSS: "danger",
  POSSIBLE_INTERNAL_SWITCH: "info",
};
const STATUS_LABEL = {
  WATCH: "Attenzione",
  RANGE_LOSS_RISK: "Rischio uscita",
  PROBABLE_RANGE_LOSS: "Probabile uscita gamma",
  POSSIBLE_INTERNAL_SWITCH: "Possibile switch interno",
};

// ---------- CSV helper (UTF-8 BOM + semicolon + escape) ----------
function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  if (/[";\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
function downloadCsv(filename, headers, rows) {
  const BOM = "\uFEFF";
  const head = headers.map((h) => csvEscape(h.label)).join(";");
  const body = rows.map((r) =>
    headers.map((h) => csvEscape(typeof h.value === "function" ? h.value(r) : r[h.key])).join(";")
  ).join("\r\n");
  const blob = new Blob([BOM + head + "\r\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}
function today() { return new Date().toISOString().slice(0, 10); }

export default function AdminIntelligencePage() {
  const [tab, setTab] = useState("REMINDER");
  return (
    <Section title="Intelligence commerciale"
             subtitle="Analisi ERP read-only — Promemoria riordino & Rischio uscita gamma">
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <Button variant={tab === "REMINDER" ? "primary" : "ghost"}
                data-testid="intel-tab-reminder"
                onClick={() => setTab("REMINDER")}>Promemoria riordino</Button>
        <Button variant={tab === "RANGE" ? "primary" : "ghost"}
                data-testid="intel-tab-range"
                onClick={() => setTab("RANGE")}>Rischio uscita gamma</Button>
      </div>
      {tab === "REMINDER" ? <ReminderTab /> : <RangeLossTab />}
    </Section>
  );
}

// ---------- Tab A: Reminder ----------
function ReminderTab() {
  const { apiGet } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [period, setPeriod] = useState("");
  const [strength, setStrength] = useState("");
  const [custQ, setCustQ] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    const q = new URLSearchParams();
    if (period) q.set("period", period);
    if (strength) q.set("strength", strength);
    apiGet(`/api/admin/intelligence/reorder-reminders?${q.toString()}`)
      .then(setData)
      .catch((e) => setErr(e?.message || String(e)));
  }, [period, strength, apiGet]);

  const rows = useMemo(() => {
    if (!data?.customers) return [];
    let out = data.customers;
    if (custQ.trim()) {
      const s = custQ.toLowerCase();
      out = out.filter((r) => (r.customer_code + " " + r.customer_name).toLowerCase().includes(s));
    }
    if (!strength) out = out.filter((r) => r.strength === "HIGH" || r.strength === "MEDIUM");
    return out;
  }, [data, strength, custQ]);

  const doExport = () => {
    const headers = [
      { label: "customer_code", key: "customer_code" },
      { label: "customer_name", key: "customer_name" },
      { label: "business_period", key: "period" },
      { label: "last_customer_order_date", key: "last_customer_order_date" },
      { label: "candidate_products_count", key: "candidate_products_count" },
      { label: "strength", key: "strength" },
      { label: "top_candidate_skus",
        value: (r) => (r.top_5_products || []).map((p) => p.sku).join("|") },
      { label: "top_candidate_products",
        value: (r) => (r.top_5_products || []).map((p) => p.product_name).join("|") },
    ];
    const period_lbl = data?.period || "P?";
    downloadCsv(`promemoria_riordino_${period_lbl}_${today()}.csv`, headers, rows);
    setFlash(`Esportazione completata · ${rows.length} righe`);
    setTimeout(() => setFlash(null), 4000);
  };

  return (
    <Card>
      <Card.Header>
        <strong>Promemoria riordino</strong>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}
                  data-testid="reminder-period-filter" style={{ padding: 6 }}>
            <option value="">Periodo (auto)</option>
            <option value="P1">P1 · Ott-Feb</option>
            <option value="P2">P2 · Mar-Mag</option>
            <option value="P3">P3 · Giu-Set</option>
          </select>
          <select value={strength} onChange={(e) => setStrength(e.target.value)}
                  data-testid="reminder-strength-filter" style={{ padding: 6 }}>
            <option value="">HIGH+MEDIUM</option>
            <option value="HIGH">Solo HIGH</option>
            <option value="MEDIUM">Solo MEDIUM</option>
            <option value="LOW">Solo LOW</option>
          </select>
          <input placeholder="Filtra cliente" value={custQ}
                 data-testid="reminder-customer-search"
                 onChange={(e) => setCustQ(e.target.value)}
                 style={{ padding: 6, minWidth: 180 }} />
          <Button size="sm" variant="secondary"
                  data-testid="reminder-export-csv"
                  onClick={doExport} disabled={rows.length === 0}>
            Esporta CSV
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {err && <Alert tone="danger">{err}</Alert>}
        {flash && <Alert tone="success">{flash}</Alert>}
        {!data && !err && <p className="muted">Calcolo in corso…</p>}
        {data && (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              Periodo: <strong>{data.period}</strong> · {data.total} clienti · {data.elapsed_ms} ms
            </p>
            {rows.length === 0
              ? <p className="muted">Nessun cliente candidato con questi filtri.</p>
              : (
                <table style={{ width: "100%", fontSize: 13 }} data-testid="reminder-table">
                  <thead>
                    <tr><th>Cliente</th><th>Strength</th><th>#SKU</th>
                        <th>Ultimo ordine cliente</th><th>Top SKU overdue</th><th></th></tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const top = r.top_5_products[0];
                      const isOpen = expanded === r.customer_id;
                      return (
                        <React.Fragment key={r.customer_id}>
                          <tr data-testid={`reminder-row-${r.customer_code}`}>
                            <td><strong>{r.customer_code}</strong> · {r.customer_name}</td>
                            <td><Badge tone={r.strength === "HIGH" ? "danger"
                                              : r.strength === "MEDIUM" ? "warning" : "neutral"}>
                                {r.strength}
                              </Badge></td>
                            <td>{r.candidate_products_count}</td>
                            <td>{r.last_customer_order_date || "—"}</td>
                            <td>{top ? `${top.sku} · ${top.days_since - (top.typical_days || 0)}g oltre` : "—"}</td>
                            <td>
                              <Button size="sm" variant="ghost"
                                      data-testid={`reminder-expand-${r.customer_code}`}
                                      onClick={() => setExpanded(isOpen ? null : r.customer_id)}>
                                {isOpen ? "Chiudi" : "Dettaglio"}
                              </Button>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr><td colSpan={6} style={{ background: "#F8FAFC", padding: 12 }}>
                              <ReminderDetail products={r.top_5_products} />
                            </td></tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

function ReminderDetail({ products }) {
  return (
    <div>
      <strong style={{ fontSize: 13 }}>Top prodotti candidati (max 5)</strong>
      <table style={{ width: "100%", marginTop: 6, fontSize: 12 }}>
        <thead>
          <tr>
            <th>SKU</th><th>Prodotto</th><th>Ultimo</th><th>Giorni</th>
            <th>Ritmo</th><th>Qtà tipica</th><th>Ultima qtà</th>
            <th>Confidence</th><th></th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => (
            <tr key={p.product_id}>
              <td><strong>{p.sku}</strong></td>
              <td>{p.product_name}</td>
              <td>{p.last_date}</td>
              <td>{p.days_since}</td>
              <td>{p.typical_days}g</td>
              <td>{p.typical_kg} kg</td>
              <td>{p.last_kg} kg</td>
              <td><Badge tone={p.confidence === "HIGH" ? "success" : "neutral"}>{p.confidence}</Badge></td>
              <td>{p.large_last_order && (
                <Badge tone="info" title="quantity_ratio ≥ 2.0">Ordine grande — attenuato</Badge>
              )}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Tab B: Range Loss ----------
function RangeLossTab() {
  const { apiGet } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [period, setPeriod] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [custQ, setCustQ] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    const q = new URLSearchParams();
    if (period) q.set("period", period);
    if (status) q.set("status", status);
    if (priority) q.set("priority", priority);
    if (categoryId) q.set("category_id", categoryId);
    apiGet(`/api/admin/intelligence/range-loss-risks?${q.toString()}`)
      .then(setData)
      .catch((e) => setErr(e?.message || String(e)));
  }, [period, status, priority, categoryId, apiGet]);

  const rows = useMemo(() => {
    if (!data?.items) return [];
    if (!custQ.trim()) return data.items;
    const s = custQ.toLowerCase();
    return data.items.filter((r) => (r.customer_code + " " + r.customer_name).toLowerCase().includes(s));
  }, [data, custQ]);

  const doExport = () => {
    const headers = [
      { label: "customer_code", key: "customer_code" },
      { label: "customer_name", key: "customer_name" },
      { label: "sku", key: "sku" },
      { label: "product_name", key: "product_name" },
      { label: "category", key: "category" },
      { label: "status", key: "commercial_status" },
      { label: "priority", key: "commercial_priority" },
      { label: "last_sku_purchase_date", key: "last_date" },
      { label: "days_since_last_purchase", key: "days_since" },
      { label: "typical_reorder_days", key: "typical_days" },
      { label: "customer_orders_after_last_sku", key: "cust_orders_after" },
      { label: "historical_order_penetration",
        value: (r) => (r.order_pen != null ? (r.order_pen * 100).toFixed(1) + "%" : "") },
      { label: "recent_penetration_3",
        value: (r) => (r.rp3 != null ? (r.rp3 * 100).toFixed(1) + "%" : "") },
      { label: "recent_penetration_5",
        value: (r) => (r.rp5 != null ? (r.rp5 * 100).toFixed(1) + "%" : "") },
      { label: "typical_order_kg", key: "typical_kg" },
      { label: "last_order_kg", key: "last_kg" },
      { label: "quantity_ratio", key: "quantity_ratio" },
      { label: "large_last_order",
        value: (r) => (r.large_last_order ? "TRUE" : "FALSE") },
      { label: "confidence", key: "confidence" },
      { label: "possible_internal_switch",
        value: (r) => (r.possible_internal_switch ? "TRUE" : "FALSE") },
      { label: "replacement_skus",
        value: (r) => (r.internal_switch_tops || []).map((s) => s.sku).join("|") },
    ];
    const period_lbl = data?.period || "P?";
    downloadCsv(`rischio_uscita_gamma_${period_lbl}_${today()}.csv`, headers, rows);
    setFlash(`Esportazione completata · ${rows.length} righe`);
    setTimeout(() => setFlash(null), 4000);
  };

  return (
    <Card>
      <Card.Header>
        <strong>Rischio uscita gamma</strong>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}
                  data-testid="range-period-filter" style={{ padding: 6 }}>
            <option value="">Periodo (auto)</option>
            <option value="P1">P1</option><option value="P2">P2</option><option value="P3">P3</option>
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  data-testid="range-status-filter" style={{ padding: 6 }}>
            <option value="">Tutti gli stati</option>
            <option value="PROBABLE_RANGE_LOSS">Probabile uscita</option>
            <option value="RANGE_LOSS_RISK">Rischio uscita</option>
            <option value="POSSIBLE_INTERNAL_SWITCH">Switch interno</option>
            <option value="WATCH">Attenzione</option>
          </select>
          <select value={priority} onChange={(e) => setPriority(e.target.value)}
                  data-testid="range-priority-filter" style={{ padding: 6 }}>
            <option value="">Tutte le priorità</option>
            <option value="HIGH">HIGH</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="LOW">LOW</option>
          </select>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
                  data-testid="range-category-filter" style={{ padding: 6, minWidth: 180 }}>
            <option value="">Tutte le categorie</option>
            {(data?.categories || []).map((c) => (
              <option key={c.id} value={c.id}>{c.name || "—"}</option>
            ))}
          </select>
          <input placeholder="Filtra cliente" value={custQ}
                 data-testid="range-customer-search"
                 onChange={(e) => setCustQ(e.target.value)}
                 style={{ padding: 6, minWidth: 180 }} />
          <Button size="sm" variant="secondary"
                  data-testid="range-export-csv"
                  onClick={doExport} disabled={rows.length === 0}>
            Esporta CSV
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {err && <Alert tone="danger">{err}</Alert>}
        {flash && <Alert tone="success">{flash}</Alert>}
        {!data && !err && <p className="muted">Calcolo in corso…</p>}
        {data && (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              Periodo: <strong>{data.period}</strong> · {data.total} SKU · {data.elapsed_ms} ms ·{" "}
              {Object.entries(data.counts).map(([k, v]) => (
                <span key={k} style={{ marginRight: 10 }}>
                  <Badge tone={STATUS_TONE[k] || "neutral"}>{STATUS_LABEL[k] || k}</Badge> {v}
                </span>
              ))}
            </p>
            {rows.length === 0
              ? <p className="muted">Nessun SKU a rischio con questi filtri.</p>
              : (
                <table style={{ width: "100%", fontSize: 12 }} data-testid="range-table">
                  <thead>
                    <tr>
                      <th>Cliente</th><th>SKU</th><th>Prodotto</th>
                      <th>Categoria</th><th>Stato</th><th>Priorità</th>
                      <th>Ultimo</th><th>Giorni</th><th>Ritmo</th>
                      <th>Dopo</th><th>Pen%</th><th>rp3</th><th>rp5</th>
                      <th>Confidence</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const key = `${r.customer_id}-${r.product_id}`;
                      const isOpen = expanded === key;
                      return (
                        <React.Fragment key={key}>
                          <tr data-testid={`range-row-${r.customer_code}-${r.sku}`}>
                            <td><strong>{r.customer_code}</strong> {r.customer_name}</td>
                            <td>{r.sku}</td>
                            <td>{r.product_name}</td>
                            <td>{r.category || "—"}</td>
                            <td><Badge tone={STATUS_TONE[r.commercial_status] || "neutral"}>
                              {STATUS_LABEL[r.commercial_status] || r.commercial_status}
                            </Badge></td>
                            <td><Badge tone={r.commercial_priority === "HIGH" ? "danger"
                                              : r.commercial_priority === "MEDIUM" ? "warning" : "neutral"}>
                              {r.commercial_priority}
                            </Badge></td>
                            <td>{r.last_date}</td>
                            <td>{r.days_since}</td>
                            <td>{r.typical_days}g</td>
                            <td>{r.cust_orders_after}</td>
                            <td>{Math.round((r.order_pen || 0) * 100)}%</td>
                            <td>{Math.round((r.rp3 || 0) * 100)}%</td>
                            <td>{Math.round((r.rp5 || 0) * 100)}%</td>
                            <td>{r.confidence}</td>
                            <td>
                              <Button size="sm" variant="ghost"
                                      data-testid={`range-expand-${r.customer_code}-${r.sku}`}
                                      onClick={() => setExpanded(isOpen ? null : key)}>
                                {isOpen ? "Chiudi" : "Perché"}
                              </Button>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr><td colSpan={15} style={{ background: "#F8FAFC", padding: 12 }}>
                              <RangeLossDetail row={r} />
                            </td></tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

function RangeLossDetail({ row }) {
  const typ = row.typical_days;
  const pen = Math.round((row.order_pen || 0) * 100);
  return (
    <div style={{ fontSize: 13, lineHeight: 1.5 }}>
      <div>
        Acquistato abitualmente ogni <strong>{typ} giorni</strong>.
        Ultimo acquisto <strong>{row.days_since} giorni fa</strong>.
        Nel frattempo il cliente ha effettuato <strong>{row.cust_orders_after} ordini</strong>{" "}
        {row.cust_orders_after_without_sku > 0
          ? <>di cui <strong>{row.cust_orders_after_without_sku}</strong> senza questo articolo. </>
          : "senza questo articolo. "}
        Presenza storica negli ordini: <strong>{pen}%</strong>.{" "}
        Ultimi 3 ordini: <strong>{Math.round((row.rp3 || 0) * 100)}%</strong>.
        Ultimi 5: <strong>{Math.round((row.rp5 || 0) * 100)}%</strong>.
      </div>
      {row.large_last_order && (
        <Alert tone="info" style={{ marginTop: 8 }}>
          Ordine molto grande (quantity_ratio {row.quantity_ratio}). Segnale attenuato — potrebbe essere scorta.
        </Alert>
      )}
      {row.possible_internal_switch && row.internal_switch_tops?.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <strong>Possibile sostituzione interna con:</strong>
          <ul style={{ margin: "4px 0 0 20px" }}>
            {row.internal_switch_tops.map((s) => (
              <li key={s.product_id}>
                <strong>{s.sku}</strong> · {s.name} — prima {s.before_kg} kg → dopo {s.after_kg} kg
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

